import { Loader2 } from 'lucide-react';
import React from 'react';
import { cn } from '@/lib/utils';

export type LoadingSize = 'sm' | 'md' | 'lg';
export type LoadingVariant = 'inline' | 'centered' | 'fullHeight' | 'overlay';

export interface LoadingStateProps {
  size?: LoadingSize;
  variant?: LoadingVariant;
  text?: string;
  className?: string;
}

const sizeClasses: Record<LoadingSize, string> = {
  sm: 'h-4 w-4',
  md: 'h-6 w-6',
  lg: 'h-8 w-8',
};

const textSizeClasses: Record<LoadingSize, string> = {
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-base',
};

const variantClasses: Record<LoadingVariant, string> = {
  inline: 'inline-flex items-center gap-2',
  centered: 'flex flex-col items-center justify-center py-8',
  fullHeight: 'flex flex-col items-center justify-center min-h-[200px]',
  overlay: 'absolute inset-0 flex flex-col items-center justify-center bg-background/80',
};

export const LoadingState: React.FC<LoadingStateProps> = ({
  size = 'md',
  variant = 'centered',
  text = 'Loading…',
  className,
}) => {
  return (
    <span role="status" aria-live="polite" className={cn(variantClasses[variant], className)}>
      <Loader2
        aria-hidden="true"
        className={cn(
          'animate-spin motion-reduce:animate-none text-muted-foreground shrink-0',
          sizeClasses[size]
        )}
      />
      {text && (
        <span
          className={cn(
            'text-muted-foreground mt-2',
            textSizeClasses[size],
            variant === 'inline' && 'mt-0'
          )}
        >
          {text}
        </span>
      )}
    </span>
  );
};

export interface LoadingSpinnerProps {
  size?: LoadingSize;
  className?: string;
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ size = 'md', className }) => {
  return (
    <Loader2
      role="status"
      aria-label="Loading…"
      className={cn(
        'animate-spin motion-reduce:animate-none text-muted-foreground',
        sizeClasses[size],
        className
      )}
    />
  );
};

interface AsyncValueProps {
  children: React.ReactNode;
  isLoading?: boolean;
  isFetching?: boolean;
  isError?: boolean;
  loadingText?: string;
}

export const AsyncValue = ({
  children,
  isLoading = false,
  isFetching = false,
  isError = false,
  loadingText = 'Loading…',
}: AsyncValueProps) => {
  if (isLoading)
    return (
      <LoadingState
        variant="inline"
        size="sm"
        text={loadingText}
        className="font-normal [&_span]:text-inherit [&_svg]:text-inherit"
      />
    );
  if (isError)
    return <span className="text-xs font-normal text-muted-foreground">Unavailable</span>;
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      {children}
      {isFetching && (
        <LoadingState
          variant="inline"
          size="sm"
          text="Updating…"
          className="font-normal [&_span]:text-inherit [&_svg]:text-inherit"
        />
      )}
    </span>
  );
};
